/**
 * AxLabs Mathematics Experiments - Sequences and Series
 *
 * Collection of experiments exploring sequences, series, and limits.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, fibonacci, factorial } from './helpers.js';
// ============================================================================
// ARITHMETIC SEQUENCE SUM
// ============================================================================
export const ArithmeticSequenceSum = createExperimentFromSchema({
    id: 'arithmetic-sequence-sum',
    name: 'Arithmetic Sequence Sum',
    domain: 'mathematics',
    description: 'Explore arithmetic sequences and their sums',
    conceptTags: ['Arithmetic Sequence', 'Series Sum', 'Linear Progression', 'Gauss Formula'],
    difficulty: 2,
    stateVariables: [
        { id: 'n', name: 'Term Index n', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'an', name: 'Term aₙ', unit: 'dimensionless', initial: 3, symbol: 'a_n' },
        { id: 'sn', name: 'Sum Sₙ', unit: 'dimensionless', initial: 3, symbol: 'S_n' },
        { id: 'average', name: 'Average of Terms', unit: 'dimensionless', initial: 3, symbol: '\\bar{x}' },
        { id: 'diff', name: 'Common Difference d', unit: 'dimensionless', initial: 2, symbol: 'd' },
    ],
    parameters: [
        { id: 'a1', name: 'First Term', unit: 'dimensionless', min: -10, max: 10, default: 1, symbol: 'a_1' },
        { id: 'd', name: 'Common Difference', unit: 'dimensionless', min: -5, max: 5, default: 2, symbol: 'd' },
        { id: 'maxN', name: 'Max Terms', unit: 'dimensionless', min: 10, max: 100, default: 20, symbol: 'n_{max}' },
        { id: 'rate', name: 'Animation Rate', unit: 'dimensionless/s', min: 0.5, max: 5, default: 1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'arithmetic-term',
            name: 'Arithmetic Sequence Term',
            latex: 'a_n = a_1 + (n-1)d',
            description: 'Formula for nth term of arithmetic sequence',
        },
        {
            id: 'arithmetic-sum',
            name: 'Arithmetic Series Sum',
            latex: 'S_n = \\frac{n}{2}(a_1 + a_n) = \\frac{n}{2}[2a_1 + (n-1)d]',
            description: 'Sum of first n terms',
        },
    ],
    derivatives: (state, params) => {
        const a1 = getParam(params, 'a1');
        const d = getParam(params, 'd');
        const maxN = Math.round(getParam(params, 'maxN'));
        const rate = getParam(params, 'rate');
        const n = getState(state, 'n');
        const nextN = Math.floor(n + rate * 0.016);
        const clampedN = Math.min(Math.max(1, nextN), maxN);
        const an = a1 + (clampedN - 1) * d;
        const sn = clampedN * (a1 + an) / 2;
        const average = clampedN > 0 ? sn / clampedN : 0;
        return {
            n: clampedN - n,
            an: an - getState(state, 'an'),
            sn: sn - getState(state, 'sn'),
            average: average - getState(state, 'average'),
            diff: d - getState(state, 'diff'),
        };
    },
    bounds: {
        n: { min: 1, max: 100 },
        an: { min: -200, max: 200 },
        sn: { min: -10000, max: 10000 },
    },
});
// ============================================================================
// GEOMETRIC SERIES CONVERGENCE
// ============================================================================
export const GeometricSeriesConvergence = createExperimentFromSchema({
    id: 'geometric-series-convergence',
    name: 'Geometric Series Convergence',
    domain: 'mathematics',
    description: 'Explore convergence and divergence of geometric series',
    conceptTags: ['Geometric Series', 'Convergence', 'Infinite Sum', 'Ratio Test'],
    difficulty: 3,
    stateVariables: [
        { id: 'n', name: 'Terms n', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'r', name: 'Ratio r', unit: 'dimensionless', initial: 0.5, symbol: 'r' },
        { id: 'partialSum', name: 'Partial Sum Sₙ', unit: 'dimensionless', initial: 1, symbol: 'S_n' },
        { id: 'infiniteSum', name: 'Infinite Sum S∞', unit: 'dimensionless', initial: 2, symbol: 'S_{\\infty}' },
        { id: 'converges', name: 'Converges', unit: 'dimensionless', initial: 1, symbol: 'C' },
        { id: 'remainder', name: 'Remainder', unit: 'dimensionless', initial: 0.5, symbol: 'R_n' },
    ],
    parameters: [
        { id: 'a1', name: 'First Term', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'a_1' },
        { id: 'r_target', name: 'Ratio r', unit: 'dimensionless', min: -2, max: 2, default: 0.5, symbol: 'r' },
        { id: 'rate', name: 'Terms per Frame', unit: 'dimensionless/s', min: 0.5, max: 5, default: 1, symbol: 'k' },
    ],
    equations: [
        {
            id: 'geometric-term',
            name: 'Geometric Sequence Term',
            latex: 'a_n = a_1 \\cdot r^{n-1}',
            description: 'Formula for nth term of geometric sequence',
        },
        {
            id: 'geometric-sum-finite',
            name: 'Finite Geometric Sum',
            latex: 'S_n = a_1\\frac{1-r^n}{1-r}',
            description: 'Sum of first n terms',
        },
        {
            id: 'geometric-sum-infinite',
            name: 'Infinite Geometric Sum',
            latex: 'S_{\\infty} = \\frac{a_1}{1-r}, \\quad |r| < 1',
            description: 'Sum converges only when |r| < 1',
        },
    ],
    derivatives: (state, params) => {
        const a1 = getParam(params, 'a1');
        const r_target = getParam(params, 'r_target');
        const rate = getParam(params, 'rate');
        const n = getState(state, 'n');
        const nextN = Math.floor(n + rate * 0.016);
        const r = r_target;
        const partialSum = Math.abs(r - 1) > 0.0001
            ? a1 * (1 - Math.pow(r, nextN)) / (1 - r)
            : a1 * nextN;
        const infiniteSum = Math.abs(r) < 1 ? a1 / (1 - r) : NaN;
        const converges = Math.abs(r) < 1 ? 1 : 0;
        const remainder = Math.abs(r) < 1 && Math.abs(r) > 0
            ? Math.abs(a1 * Math.pow(r, nextN) / (1 - r))
            : 0;
        return {
            n: nextN - n,
            r: r - getState(state, 'r'),
            partialSum: partialSum - getState(state, 'partialSum'),
            infiniteSum: (infiniteSum || getState(state, 'infiniteSum')) - getState(state, 'infiniteSum'),
            converges: converges - getState(state, 'converges'),
            remainder: remainder - getState(state, 'remainder'),
        };
    },
    bounds: {
        n: { min: 1, max: 1000 },
        partialSum: { min: -1000, max: 1000 },
        infiniteSum: { min: -10, max: 10 },
    },
});
// ============================================================================
// FIBONACCI RATIO CONVERGENCE
// ============================================================================
export const FibonacciRatioConvergence = createExperimentFromSchema({
    id: 'fibonacci-ratio-convergence',
    name: 'Fibonacci Ratio Convergence',
    domain: 'mathematics',
    description: 'Explore Fibonacci sequence and golden ratio convergence',
    conceptTags: ['Fibonacci', 'Golden Ratio', 'Recursive Sequences', 'Phi'],
    difficulty: 3,
    stateVariables: [
        { id: 'n', name: 'Index n', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'fn', name: 'Fₙ', unit: 'dimensionless', initial: 1, symbol: 'F_n' },
        { id: 'fn1', name: 'Fₙ₊₁', unit: 'dimensionless', initial: 1, symbol: 'F_{n+1}' },
        { id: 'ratio', name: 'Fₙ₊₁/Fₙ', unit: 'dimensionless', initial: 1, symbol: 'F_{n+1}/F_n' },
        { id: 'phi', name: 'Golden Ratio φ', unit: 'dimensionless', initial: 1.618, symbol: '\\varphi' },
        { id: 'error', name: '|ratio - φ|', unit: 'dimensionless', initial: 0.618, symbol: 'Error' },
    ],
    parameters: [
        { id: 'rate', name: 'Terms per Frame', unit: 'dimensionless/s', min: 0.5, max: 5, default: 1, symbol: 'r' },
        { id: 'f1', name: 'F₁', unit: 'dimensionless', min: 1, max: 10, default: 1, symbol: 'F_1' },
        { id: 'f2', name: 'F₂', unit: 'dimensionless', min: 1, max: 10, default: 1, symbol: 'F_2' },
    ],
    equations: [
        {
            id: 'fibonacci-recursive',
            name: 'Fibonacci Recursion',
            latex: 'F_n = F_{n-1} + F_{n-2}',
            description: 'Each term is sum of previous two',
        },
        {
            id: 'golden-ratio',
            name: 'Golden Ratio',
            latex: '\\varphi = \\frac{1 + \\sqrt{5}}{2} \\approx 1.618',
            description: 'Limit of consecutive Fibonacci number ratios',
        },
        {
            id: 'binet',
            name: 'Binet\'s Formula',
            latex: 'F_n = \\frac{\\varphi^n - \\psi^n}{\\sqrt{5}}, \\quad \\psi = \\frac{1-\\sqrt{5}}{2}',
            description: 'Closed-form formula for Fibonacci numbers',
        },
    ],
    derivatives: (state, params) => {
        const rate = getParam(params, 'rate');
        void getParam(params, 'f1'); // Unused parameter
        void getParam(params, 'f2'); // Unused parameter
        const n = getState(state, 'n');
        const nextN = Math.floor(n + rate * 0.016);
        const fn = fibonacci(nextN);
        const fn1 = fibonacci(nextN + 1);
        const ratio = fn > 0 ? fn1 / fn : 0;
        const phi = (1 + Math.sqrt(5)) / 2;
        const error = Math.abs(ratio - phi);
        return {
            n: nextN - n,
            fn: fn - getState(state, 'fn'),
            fn1: fn1 - getState(state, 'fn1'),
            ratio: ratio - getState(state, 'ratio'),
            phi: phi - getState(state, 'phi'),
            error: error - getState(state, 'error'),
        };
    },
    bounds: {
        n: { min: 1, max: 100 },
        fn: { min: 0, max: 1e16 },
        ratio: { min: 1, max: 2 },
    },
});
// ============================================================================
// HARMONIC SERIES DIVERGENCE
// ============================================================================
export const HarmonicSeriesDivergence = createExperimentFromSchema({
    id: 'harmonic-series-divergence',
    name: 'Harmonic Series Divergence',
    domain: 'mathematics',
    description: 'Explore slow divergence of the harmonic series',
    conceptTags: ['Harmonic Series', 'Divergence', 'p-Series', 'Logarithmic Growth'],
    difficulty: 4,
    stateVariables: [
        { id: 'n', name: 'Terms n', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'hn', name: 'Hₙ Sum', unit: 'dimensionless', initial: 1, symbol: 'H_n' },
        { id: 'lnN', name: 'ln(n) + γ', unit: 'dimensionless', initial: 0.577, symbol: '\\ln(n) + \\gamma' },
        { id: 'difference', name: 'Hₙ - ln(n)', unit: 'dimensionless', initial: 0.423, symbol: 'H_n - \\ln(n)' },
        { id: 'pSeries', name: 'p-Series Sum', unit: 'dimensionless', initial: 1, symbol: '\\zeta(p)' },
    ],
    parameters: [
        { id: 'p', name: 'p value', unit: 'dimensionless', min: 0.5, max: 3, default: 1, symbol: 'p' },
        { id: 'rate', name: 'Terms per Frame', unit: 'dimensionless/s', min: 1, max: 20, default: 5, symbol: 'r' },
    ],
    equations: [
        {
            id: 'harmonic-series',
            name: 'Harmonic Series',
            latex: 'H_n = \\sum_{k=1}^n \\frac{1}{k}',
            description: 'Sum of reciprocals of natural numbers',
        },
        {
            id: 'harmonic-divergence',
            name: 'Harmonic Divergence',
            latex: '\\lim_{n\\to\\infty} H_n = \\infty',
            description: 'Harmonic series diverges (very slowly)',
        },
        {
            id: 'euler-constant',
            name: 'Euler-Mascheroni Constant',
            latex: '\\gamma = \\lim_{n\\to\\infty}\\left(H_n - \\ln n\\right) \\approx 0.577',
            description: 'Difference approaches this constant',
        },
        {
            id: 'p-series',
            name: 'p-Series',
            latex: '\\sum_{k=1}^\\infty \\frac{1}{k^p} \\text{ converges iff } p > 1',
            description: 'Generalized harmonic series convergence condition',
        },
    ],
    derivatives: (state, params) => {
        const p = getParam(params, 'p');
        const rate = getParam(params, 'rate');
        const n = getState(state, 'n');
        const nextN = Math.floor(n + rate * 0.016);
        // Compute harmonic sum
        let hn = 0;
        let pSeriesSum = 0;
        for (let k = 1; k <= nextN; k++) {
            hn += 1 / k;
            pSeriesSum += 1 / Math.pow(k, p);
        }
        const gamma = 0.5772156649;
        const lnN = Math.log(nextN) + gamma;
        const difference = hn - Math.log(nextN);
        return {
            n: nextN - n,
            hn: hn - getState(state, 'hn'),
            lnN: lnN - getState(state, 'lnN'),
            difference: difference - getState(state, 'difference'),
            pSeries: pSeriesSum - getState(state, 'pSeries'),
        };
    },
    bounds: {
        n: { min: 1, max: 100000 },
        hn: { min: 1, max: 15 },
        lnN: { min: 0, max: 12 },
    },
});
// ============================================================================
// RECURSIVE SEQUENCE BEHAVIOR
// ============================================================================
export const RecursiveSequenceBehavior = createExperimentFromSchema({
    id: 'recursive-sequence-behavior',
    name: 'Recursive Sequence Behavior',
    domain: 'mathematics',
    description: 'Explore behavior of various recursively defined sequences',
    conceptTags: ['Recursive Sequences', 'Recurrence Relations', 'Fixed Points', 'Convergence'],
    difficulty: 5,
    stateVariables: [
        { id: 'n', name: 'Index n', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'xn', name: 'xₙ', unit: 'dimensionless', initial: 0.5, symbol: 'x_n' },
        { id: 'limit', name: 'Limit L', unit: 'dimensionless', initial: 0.618, symbol: 'L' },
        { id: 'fixedPoint', name: 'Fixed Point', unit: 'dimensionless', initial: 0.618, symbol: 'f(L)=L' },
        { id: 'converges', name: 'Converges', unit: 'dimensionless', initial: 1, symbol: 'C' },
    ],
    parameters: [
        { id: 'sequenceType', name: 'Sequence Type', unit: 'dimensionless', min: 0, max: 4, default: 0, symbol: 'S' },
        { id: 'x0', name: 'Initial x₀', unit: 'dimensionless', min: 0, max: 10, default: 1, symbol: 'x_0' },
        { id: 'param', name: 'Parameter', unit: 'dimensionless', min: 0.1, max: 5, default: 1, symbol: 'c' },
    ],
    equations: [
        {
            id: 'recursive-general',
            name: 'Recursive Definition',
            latex: 'x_{n+1} = f(x_n)',
            description: 'Each term depends on previous term',
        },
        {
            id: 'fixed-point',
            name: 'Fixed Point',
            latex: 'L = f(L)',
            description: 'Limit must satisfy fixed point equation',
        },
        {
            id: 'banach',
            name: 'Banach Fixed Point Theorem',
            latex: '|f\'(x)| < 1 \\implies \\text{convergence to fixed point}',
            description: 'Contractive mappings converge',
        },
    ],
    derivatives: (state, params) => {
        const sequenceType = Math.round(getParam(params, 'sequenceType'));
        const x0 = getParam(params, 'x0');
        const c = getParam(params, 'param');
        const n = getState(state, 'n');
        const nextN = n + 1;
        // Compute next term based on sequence type
        let xn = x0;
        let limit = 0;
        let fixedPoint = 0;
        let converges = 1;
        // Recompute from scratch for current n
        let current = x0;
        for (let i = 0; i < nextN; i++) {
            switch (sequenceType) {
                case 0: // x_{n+1} = 1/(1 + x_n)
                    current = 1 / (1 + current);
                    limit = (Math.sqrt(5) - 1) / 2;
                    fixedPoint = (Math.sqrt(5) - 1) / 2;
                    break;
                case 1: // x_{n+1} = sqrt(c + x_n)
                    current = Math.sqrt(c + current);
                    limit = (1 + Math.sqrt(1 + 4 * c)) / 2;
                    fixedPoint = (1 + Math.sqrt(1 + 4 * c)) / 2;
                    break;
                case 2: // x_{n+1} = c * x_n * (1 - x_n) (logistic)
                    current = c * current * (1 - current);
                    if (c <= 1) {
                        limit = 0;
                        fixedPoint = 0;
                    }
                    else if (c < 3) {
                        limit = (c - 1) / c;
                        fixedPoint = (c - 1) / c;
                    }
                    else {
                        converges = 0;
                        limit = NaN;
                        fixedPoint = (c - 1) / c;
                    }
                    break;
                case 3: // x_{n+1} = (x_n + c/x_n)/2 (Babylonian for sqrt)
                    current = (current + c / current) / 2;
                    limit = Math.sqrt(c);
                    fixedPoint = Math.sqrt(c);
                    break;
                case 4: // x_{n+1} = cos(x_n)
                    current = Math.cos(current);
                    limit = 0.739085;
                    fixedPoint = 0.739085;
                    break;
            }
        }
        xn = current;
        return {
            n: 1,
            xn: xn - getState(state, 'xn'),
            limit: (isNaN(limit) ? getState(state, 'limit') : limit) - getState(state, 'limit'),
            fixedPoint: fixedPoint - getState(state, 'fixedPoint'),
            converges: converges - getState(state, 'converges'),
        };
    },
    bounds: {
        n: { min: 1, max: 100 },
        xn: { min: -5, max: 5 },
    },
});
// ============================================================================
// PARTIAL SUM VISUALIZATION
// ============================================================================
export const PartialSumVisualization = createExperimentFromSchema({
    id: 'partial-sum-visualization',
    name: 'Partial Sum Visualization',
    domain: 'mathematics',
    description: 'Visualize partial sums converging to series value',
    conceptTags: ['Partial Sums', 'Series Convergence', 'Summation', 'Telescoping'],
    difficulty: 4,
    stateVariables: [
        { id: 'n', name: 'Terms n', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'term', name: 'Current Term aₙ', unit: 'dimensionless', initial: 0.5, symbol: 'a_n' },
        { id: 'partialSum', name: 'Partial Sum Sₙ', unit: 'dimensionless', initial: 0.5, symbol: 'S_n' },
        { id: 'target', name: 'Target Sum', unit: 'dimensionless', initial: 1, symbol: 'S' },
        { id: 'error', name: 'Error |S - Sₙ|', unit: 'dimensionless', initial: 0.5, symbol: 'Error' },
    ],
    parameters: [
        { id: 'seriesType', name: 'Series Type', unit: 'dimensionless', min: 0, max: 5, default: 0, symbol: 'S' },
        { id: 'rate', name: 'Terms per Frame', unit: 'dimensionless/s', min: 0.5, max: 5, default: 1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'partial-sum-def',
            name: 'Partial Sum Definition',
            latex: 'S_n = \\sum_{k=1}^n a_k',
            description: 'Sum of first n terms',
        },
        {
            id: 'series-sum',
            name: 'Series Sum',
            latex: 'S = \\lim_{n\\to\\infty} S_n',
            description: 'Limit of partial sums (if convergent)',
        },
    ],
    derivatives: (state, params) => {
        const seriesType = Math.round(getParam(params, 'seriesType'));
        const rate = getParam(params, 'rate');
        const n = getState(state, 'n');
        const nextN = Math.floor(n + rate * 0.016);
        let term = 0;
        let partialSum = 0;
        let target = 0;
        for (let k = 1; k <= nextN; k++) {
            switch (seriesType) {
                case 0: // 1/2^n = 1
                    term = 1 / Math.pow(2, k);
                    target = 1;
                    break;
                case 1: // 1/2^k from k=0 = 2
                    term = 1 / Math.pow(2, k - 1);
                    target = 2;
                    break;
                case 2: // 1/n(n+1) = 1 (telescoping)
                    term = 1 / (k * (k + 1));
                    target = 1;
                    break;
                case 3: // 1/n! = e - 1
                    term = 1 / factorial(k);
                    target = Math.E - 1;
                    break;
                case 4: // (-1)^n / n! = 1/e
                    term = Math.pow(-1, k) / factorial(k);
                    target = 1 / Math.E;
                    break;
                case 5: // 1/k^2 = pi^2/6
                    term = 1 / (k * k);
                    target = Math.PI * Math.PI / 6;
                    break;
            }
            partialSum += term;
        }
        const error = Math.abs(target - partialSum);
        return {
            n: nextN - n,
            term: term - getState(state, 'term'),
            partialSum: partialSum - getState(state, 'partialSum'),
            target: target - getState(state, 'target'),
            error: error - getState(state, 'error'),
        };
    },
    bounds: {
        n: { min: 1, max: 100 },
        partialSum: { min: -2, max: 3 },
        error: { min: 0, max: 2 },
    },
});
// ============================================================================
// LIMIT APPROXIMATION
// ============================================================================
export const LimitApproximation = createExperimentFromSchema({
    id: 'limit-approximation',
    name: 'Limit Approximation',
    domain: 'mathematics',
    description: 'Numerically approximate limits of sequences',
    conceptTags: ['Limits', 'Sequences', 'Convergence', 'Numerical Methods', 'e', 'π'],
    difficulty: 5,
    stateVariables: [
        { id: 'n', name: 'Index n', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'an', name: 'aₙ', unit: 'dimensionless', initial: 2, symbol: 'a_n' },
        { id: 'limit', name: 'Limit L', unit: 'dimensionless', initial: 2.718, symbol: 'L' },
        { id: 'error', name: '|aₙ - L|', unit: 'dimensionless', initial: 0.718, symbol: '|a_n - L|' },
        { id: 'rateConv', name: 'Conv Rate', unit: 'dimensionless', initial: 1, symbol: '\\rho' },
    ],
    parameters: [
        { id: 'limitType', name: 'Limit Type', unit: 'dimensionless', min: 0, max: 6, default: 0, symbol: 'L' },
        { id: 'stepsPerFrame', name: 'Steps per Frame', unit: 'dimensionless', min: 1, max: 20, default: 2, symbol: 'k' },
    ],
    equations: [
        {
            id: 'limit-def',
            name: 'Limit Definition',
            latex: '\\lim_{n\\to\\infty} a_n = L \\iff \\forall\\epsilon>0, \\exists N: n>N \\implies |a_n - L| < \\epsilon',
            description: 'Formal epsilon-N definition of limit',
        },
        {
            id: 'rate-convergence',
            name: 'Rate of Convergence',
            latex: '|a_{n+1} - L| \\approx \\rho |a_n - L|',
            description: 'Linear convergence when 0 < ρ < 1',
        },
    ],
    derivatives: (state, params) => {
        const limitType = Math.round(getParam(params, 'limitType'));
        const stepsPerFrame = Math.round(getParam(params, 'stepsPerFrame'));
        const n = getState(state, 'n');
        const nextN = n + stepsPerFrame;
        let an = 0;
        let limit = 0;
        switch (limitType) {
            case 0: // (1 + 1/n)^n → e
                an = Math.pow(1 + 1 / nextN, nextN);
                limit = Math.E;
                break;
            case 1: // n * sin(1/n) → 1
                an = nextN * Math.sin(1 / nextN);
                limit = 1;
                break;
            case 2: // (1 + 1/n)^n → e (alt)
                an = Math.pow(1 + 1 / nextN, nextN);
                limit = Math.E;
                break;
            case 3: // ln(1 + 1/n)^n → 1
                an = nextN * Math.log(1 + 1 / nextN);
                limit = 1;
                break;
            case 4: // Product (1 - 1/k^2) from k=2 to n → 1/2
                let prod = 1;
                for (let k = 2; k <= nextN; k++) {
                    prod *= (1 - 1 / (k * k));
                }
                an = prod;
                limit = 0.5;
                break;
            case 5: // Wallis product for π/2
                let wallis = 1;
                for (let k = 1; k <= nextN; k++) {
                    wallis *= (4 * k * k) / (4 * k * k - 1);
                }
                an = wallis;
                limit = Math.PI / 2;
                break;
            case 6: // sqrt(2 + sqrt(2 + ... n times)) → 2
                let nested = 0;
                for (let i = 0; i < nextN; i++) {
                    nested = Math.sqrt(2 + nested);
                }
                an = nested;
                limit = 2;
                break;
        }
        const error = Math.abs(an - limit);
        const prevError = getState(state, 'error');
        const rateConv = prevError > 0.0001 ? error / prevError : 1;
        return {
            n: stepsPerFrame,
            an: an - getState(state, 'an'),
            limit: limit - getState(state, 'limit'),
            error: error - prevError,
            rateConv: rateConv - getState(state, 'rateConv'),
        };
    },
    bounds: {
        n: { min: 1, max: 10000 },
        an: { min: 0, max: 5 },
        error: { min: 0, max: 5 },
    },
});
//# sourceMappingURL=sequences.js.map